import type { FormKitNode } from '@formkit/core';
import { findBandByFrequency } from '@/utils/helpers';

interface FormData {
  freq?: string;
  band?: string;
  [key: string]: any;
}

/**
 * Custom validation rule to ensure frequency and band match
 * @param node - The FormKit node
 * @returns boolean indicating if validation passes
 */
export function frequencyBandMatch(node: FormKitNode): boolean {
  // Get the parent form node
  let formNode = node;
  while (formNode.parent && formNode.name !== 'form') {
    formNode = formNode.parent;
  }

  const formData = formNode.value as FormData | undefined;

  if (!formData || !formData.freq || !formData.band) {
    // If either field is empty, consider it valid (let required validation handle emptiness)
    return true;
  }

  // Convert frequency from MHz to kHz
  const freqKHz = Number(formData.freq) * 1000;

  // Find the band that corresponds to this frequency
  const expectedBand = findBandByFrequency(freqKHz);

  // If no band found for this frequency, it's invalid
  if (!expectedBand) {
    return false;
  }

  // Check if the selected band matches the expected band
  return formData.band === expectedBand;
}


/**
 * Custom validation rule to ensure park codes are properly formatted
 * Validates comma-separated park codes in format: AA-NNNN (e.g., US-1234)
 * @param node - The FormKit node
 * @returns boolean indicating if validation passes
 */
export function parkCodeFormat(node: FormKitNode): boolean {
  // Handle empty or undefined values
  if (!node.value || typeof node.value !== 'string') {
    return true; // Let required validation handle empty values
  }

  const value = node.value.trim();
  if (!value) {
    return true;
  }

  // Split by comma, trim whitespace, and filter out empty strings
  const parkCodes = value.split(',')
    .map(code => code.trim())
    .filter(code => code.length > 0);

  // If no valid codes after filtering, return true (empty input)
  if (parkCodes.length === 0) {
    return true;
  }

  // Validate each park code format: 2 letters (case insensitive), hyphen, 4-5 digits
  const parkCodeRegex = /^[A-Za-z]{2}-\d{4,5}$/;

  return parkCodes.every(code => parkCodeRegex.test(code));
}

/**
 * Custom validation messages
 */
export const customValidationMessages = {
  frequencyBandMatch: 'Band and Frequency do not match',
  parkCodeFormat: 'Unknown Park Ref format. Separate multiple parks with commas.'
};